"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "kitchen-space-planner",
  "food-storage-calculator",
  "refrigerator-size-calculator",
  "food-waste-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Organization Planner",
  category: "Space Planning" as const,
  mediumDescription:
    "Map your fridge by zone, USDA-style: bottom shelf for raw meat, middle shelves for dairy and eggs, door for condiments only. Built on USDA Food Safety guidance and FDA Food Code 3-501.16 (cold storage at ≤40°F).",
  faqs: [
    { question: "Why store raw meat on the bottom shelf?", answer: "USDA Food Safety guidance recommends raw meat, poultry, and seafood on the bottom shelf so any drips can't contaminate ready-to-eat food below. The bottom shelf also runs coldest in most household fridges." },
    { question: "What temperature should my fridge be?", answer: "FDA Food Code 3-501.16 sets the cold-holding maximum at 41°F (5°C); USDA recommends 40°F (4°C) or below for home fridges. The freezer should be 0°F (−18°C) or below." },
    { question: "Why shouldn't I store milk in the door?", answer: "Door shelves see the largest temperature swings — every door open warms them. Milk and eggs spoil faster there. Use the door for condiments, juice, and butter you'll use within a week." },
    { question: "What's the difference between high and low humidity crispers?", answer: "High humidity (vent closed) keeps leafy greens and herbs from wilting. Low humidity (vent open) lets ethylene gas escape, which is what you want for apples, pears, and stone fruit." },
    { question: "How often should I clean the fridge?", answer: "Wipe spills immediately. Deep-clean every 3 months: empty fully, wash shelves with warm soapy water, and check the door gasket for tears. Buildup on the gasket lets warm air in and raises energy use." },
    { question: "Why does the planner change with fridge type?", answer: "French door, side-by-side, top-freezer, and bottom-freezer layouts have different cold zones and shelf heights. The planner adapts the storage map and gives layout-specific tips for each style." },
  ],
  relatedTools: ["kitchen-space-planner", "food-storage-calculator", "refrigerator-size-calculator"],
}

interface ZoneGuide {
  zone: string
  temperature: string
  bestFor: string[]
  avoid: string[]
}

export function FridgeOrganizationPlannerPageClient() {
  const [fridgeType, setFridgeType] = useState("french-door")
  const [householdSize, setHouseholdSize] = useState("3")

  // Original zone data preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const size = readCount(householdSize, { min: 1, max: 20 })
    if (size === null) return null

    const layout: ZoneGuide[] = [
      {
        zone: "Top shelf",
        temperature: "37–40°F (warmest in main compartment)",
        bestFor: ["Leftovers (clear containers)", "Drinks & beverages", "Ready-to-eat foods", "Herbs in water", "Tortillas"],
        avoid: ["Raw meat (drip risk)", "Eggs (temperature swings)"],
      },
      {
        zone: "Middle shelves",
        temperature: "35–38°F (most consistent)",
        bestFor: ["Dairy (milk, yogurt, cheese)", "Eggs in original carton", "Butter", "Sealed deli meats", "Prepared foods"],
        avoid: ["Raw meat (use bottom shelf)"],
      },
      {
        zone: "Bottom shelf",
        temperature: "33–36°F (coldest)",
        bestFor: ["Raw meat, poultry, fish", "Marinating items", "Thawing items", "Seafood"],
        avoid: ["Ready-to-eat foods (cross-contamination)", "Cold-sensitive produce"],
      },
      {
        zone: "Door shelves",
        temperature: "40–45°F (warmest, most fluctuation)",
        bestFor: ["Condiments (ketchup, mustard, mayo)", "Salad dressings", "Soy sauce, hot sauce", "Pickles, olives", "Butter (if used quickly)", "Juice (if consumed within days)"],
        avoid: ["Milk (spoils faster)", "Eggs (temperature swings)", "Fresh meat or fish"],
      },
      {
        zone: "Crisper (high humidity, vent closed)",
        temperature: "32–40°F with humidity control",
        bestFor: ["Leafy greens", "Broccoli, cauliflower", "Carrots, celery", "Fresh herbs", "Berries (original container)"],
        avoid: ["Ethylene-producing fruits (apples, pears)"],
      },
      {
        zone: "Crisper (low humidity, vent open)",
        temperature: "32–40°F with humidity control",
        bestFor: ["Apples, pears", "Grapes", "Citrus fruits", "Peppers", "Mushrooms (paper bag)"],
        avoid: ["Leafy greens (will wilt)"],
      },
    ]

    const tips: string[] = []
    tips.push("Keep the fridge at 40°F (4°C) or below — FDA Food Code")
    tips.push("Store raw meat on the bottom shelf (USDA)")
    tips.push("Use leftovers within 3–4 days (USDA)")
    tips.push("Don't overfill — air needs to circulate")
    tips.push("Use clear containers and label everything with date")
    if (size >= 4) {
      tips.push("With 4+ people, assign zones for each family member's items")
      tips.push("Use bins to group snacks, lunch items, and prep")
    }
    if (fridgeType === "side-by-side") {
      tips.push("Side-by-side: narrow shelves limit pizza boxes and party platters")
    } else if (fridgeType === "top-freezer") {
      tips.push("Top-freezer: bottom shelf is coldest — use for meat and dairy")
    } else if (fridgeType === "bottom-freezer") {
      tips.push("Bottom-freezer: eye-level fridge keeps fresh food in sight")
    }

    return { layout, tips, fridgeType }
  }, [fridgeType, householdSize])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Where every"
      heroTitleItalic="thing"
      heroTitlePost="actually goes."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "USDA / FDA cold-storage guidance"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "40°F", label: "FDA cold max", note: "Code 3-501.16" },
            { value: "0°F", label: "USDA freezer", note: "Or below" },
            { value: "3–4", label: "Days for leftovers", note: "USDA" },
            { value: "6", label: "Storage zones", note: "Mapped" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-organization-planner")}
      cta={{
        title: "Shop fridges built for organization.",
        body: "Browse French door fridges with adjustable shelves, dual crispers, and full-extension drawers — the layouts that make USDA-style zoning easy.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Pick your fridge,
                <br />
                size your family.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Layout adapts to fridge style; tips adapt to household size.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Fridge type
                  </label>
                  <select
                    value={fridgeType}
                    onChange={(e) => setFridgeType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="french-door">French door</option>
                    <option value="side-by-side">Side-by-side</option>
                    <option value="top-freezer">Top-freezer</option>
                    <option value="bottom-freezer">Bottom-freezer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Household size
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    step={1}
                    value={householdSize}
                    onChange={(e) => setHouseholdSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Larger households get extra zone-assignment tips.
                  </div>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Storage map
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {result?.layout.map((z) => (
                      <div key={z.zone} className="rounded-lg p-3 bg-white/10 border border-white/15">
                        <div className="text-[14px] font-black tracking-tight">{z.zone}</div>
                        <div className="text-[11px] text-white/70 mt-0.5">{z.temperature}</div>
                        <div className="text-[12px] text-white/85 mt-1.5 leading-[1.4]">
                          <span className="font-bold">Best for:</span> {z.bestFor.slice(0, 3).join(", ")}
                          {z.bestFor.length > 3 && "…"}
                        </div>
                      </div>
                    )) ?? (
                      <div className="text-[14px] text-white/80">Pick a fridge type to see the storage map.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Layouts that organize"
          title="French door fridges with flexible shelves"
          description="Adjustable shelves and dual crispers make USDA-style zoning easy."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Built on <em className="italic text-[#ff9951]">USDA</em> food-safety zoning.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                The map follows USDA Food Safety guidance for raw vs ready-to-eat foods, FDA Food Code 3-501.16 cold-holding, and standard humidity-drawer practice for produce.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Raw on the bottom",
                  d: "USDA: raw meat, poultry, and seafood on the bottom shelf so drips can't contaminate ready-to-eat food.",
                },
                {
                  n: "02",
                  t: "Door for condiments only",
                  d: "Door shelves swing through the largest temperature range — fine for ketchup, terrible for milk and eggs.",
                },
                {
                  n: "03",
                  t: "Dual crispers",
                  d: "High humidity (vent closed) for leafy greens. Low humidity (vent open) for ethylene-producing fruits.",
                },
                {
                  n: "04",
                  t: "Cold thresholds",
                  d: "FDA Food Code 3-501.16: 41°F max for cold holding. USDA: 40°F or below for home fridges. Freezer: 0°F or below.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer fridges with eye-level fresh food"
          description="Fresh-food compartment at eye level — easier to scan and organize than top-freezer layouts."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
